import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { ActionButtons } from '../action-buttons/ActionButtons';
import { DocumentListing } from '../document-listing/DocumentListing';
import { InlineAlert } from '../inline-alert/InlineAlert';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';
import { PdfDocumentPreview } from '../pdf-document-preview/PdfDocumentPreview';

import { buildSidebarDocumentActionButtons } from './buildSidebarDocumentActionButtons';
import { isDocumentPreviewEnabled } from './isDocumentPreviewEnabled';
import { SidebarDocumentPanelProps } from './types';

import styles from './sidebar-document-panel.module.scss';

export function SidebarDocumentPanel({
  documents,
  error,
  listingClassName,
  loading,
  moreEntriesCount,
  onDocumentClick,
  onDocumentDownload,
  onOpenInPage,
  openInPageButtonText,
  selectedDocument,
  selectedDocumentPreviewUrl,
  showActionButtons = true,
}: SidebarDocumentPanelProps) {
  const { t } = useTranslation();

  const actionButtons = useMemo(
    () =>
      buildSidebarDocumentActionButtons({
        documents,
        onDocumentDownload,
        onOpenInPage,
        openInPageButtonText,
        selectedDocument,
        t,
      }),
    [
      documents,
      onDocumentDownload,
      onOpenInPage,
      openInPageButtonText,
      selectedDocument,
      t,
    ]
  );

  const renderDocumentView = () => {
    if (loading) {
      return (
        <LoadingIndicator
          size="2em"
          style={{ margin: '2em auto' }}
          data-testid="loading-indicator"
        />
      );
    }

    if (error) {
      return (
        <InlineAlert className={styles['alert']}>
          <p>
            {t('app.pageSidebar.errorText')}
            <br />
            {error?.message}
          </p>
        </InlineAlert>
      );
    }

    return (
      <>
        {onDocumentClick && (
          <DocumentListing
            appearance="panel"
            className={clsx(
              selectedDocumentPreviewUrl && styles['show-preview'],
              listingClassName
            )}
            documents={documents}
            moreEntriesCount={moreEntriesCount}
            onDocumentClick={onDocumentClick}
            selectedDocument={selectedDocument}
          />
        )}

        {selectedDocument && !isDocumentPreviewEnabled(selectedDocument) && (
          <div className={styles['no-preview-available']}>
            {selectedDocument.virusFree == null
              ? t('app.pageSidebar.previewUnavailableNotScanned')
              : selectedDocument.virusFree === false
                ? t('app.pageSidebar.previewUnavailableMalicious')
                : t('app.pageSidebar.noPreviewAvailable')}
          </div>
        )}

        {selectedDocument &&
          isDocumentPreviewEnabled(selectedDocument) &&
          selectedDocumentPreviewUrl && (
            <div className={styles['preview']}>
              <PdfDocumentPreview
                ariaLabel={t('app.pdfDocumentPreview.title')}
                filename={selectedDocument.fileName ?? ''}
                key={selectedDocumentPreviewUrl}
                source={{
                  type: 'url',
                  url: selectedDocumentPreviewUrl,
                }}
              />
            </div>
          )}
      </>
    );
  };

  const hasActionButtons =
    showActionButtons && (onOpenInPage != null || onDocumentDownload != null);

  return (
    <div className={styles['root']} data-testid="sidebar-document-panel">
      <div className={styles['document-listing-container']}>
        {renderDocumentView()}
      </div>
      {hasActionButtons ? (
        <div className={styles['footer-actions']}>
          <ActionButtons buttons={actionButtons} />
        </div>
      ) : null}
    </div>
  );
}
